import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/layouts/DashboardLayout';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator, FileText, Users, Package, Layers, Award, Shield,
  ClipboardList, UserCheck, Car, Megaphone, BarChart3, Settings,
  Receipt, ShoppingCart, Building2, Target, Bell, TrendingUp
} from 'lucide-react';

// Sayfa tanımları - her sayfanın ikonu, başlığı ve açıklaması
const PAGE_DEFINITIONS = {
  'calculator': {
    icon: Calculator,
    title: 'Hesaplama',
    description: 'Sipariş tutarını hesaplayın',
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50'
  },
  'price-lists': {
    icon: FileText,
    title: 'Fiyat Listeleri',
    description: 'Güncel fiyat listelerini görüntüleyin',
    color: 'bg-green-500',
    bgColor: 'bg-green-50'
  },
  'bonus': {
    icon: Award,
    title: 'Hedef & Prim',
    description: 'Prim hesaplama ve hedef takibi',
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-50'
  },
  'crm': {
    icon: Users,
    title: 'CRM',
    description: 'Müşteri yönetimi ve takibi',
    color: 'bg-purple-500',
    bgColor: 'bg-purple-50'
  },
  'insurance': {
    icon: Shield,
    title: 'Sigorta',
    description: 'Sigorta işlemleri',
    color: 'bg-cyan-500',
    bgColor: 'bg-cyan-50'
  },
  'tasks': {
    icon: ClipboardList,
    title: 'Görevler',
    description: 'Görev yönetimi',
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50'
  },
  'preorders': {
    icon: ShoppingCart,
    title: 'Ön Sipariş',
    description: 'Ön sipariş takibi',
    color: 'bg-pink-500',
    bgColor: 'bg-pink-50'
  },
  'purchases': {
    icon: Receipt,
    title: 'Şube Satın Alma',
    description: 'Şube ihtiyaçları satın alma',
    color: 'bg-violet-500',
    bgColor: 'bg-violet-50'
  },
  'procurement-decision': {
    icon: Car,
    title: 'Motor Alış Karar',
    description: 'Motor satın alma kararları',
    color: 'bg-indigo-500',
    bgColor: 'bg-indigo-50'
  },
  'performance': {
    icon: BarChart3,
    title: 'Performans',
    description: 'Performans raporları',
    color: 'bg-teal-500',
    bgColor: 'bg-teal-50'
  },
  'refunds': {
    icon: Receipt,
    title: 'İade Yönetimi',
    description: 'İade işlemleri',
    color: 'bg-red-500',
    bgColor: 'bg-red-50'
  },
  'brands': {
    icon: Package,
    title: 'Markalar',
    description: 'Marka yönetimi',
    color: 'bg-slate-500',
    bgColor: 'bg-slate-50'
  },
  'models': {
    icon: Layers,
    title: 'Modeller',
    description: 'Model yönetimi',
    color: 'bg-emerald-500',
    bgColor: 'bg-emerald-50'
  },
  'campaigns': {
    icon: Megaphone,
    title: 'Kampanyalar',
    description: 'Kampanya yönetimi',
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50'
  },
  'users': {
    icon: UserCheck,
    title: 'Kullanıcılar',
    description: 'Kullanıcı yönetimi',
    color: 'bg-rose-500',
    bgColor: 'bg-rose-50'
  },
  'branches': {
    icon: Building2,
    title: 'Şubeler',
    description: 'Şube yönetimi',
    color: 'bg-lime-500',
    bgColor: 'bg-lime-50'
  },
  'factory-prices': {
    icon: Target,
    title: 'Fabrika Fiyatları',
    description: 'Fabrika fiyat yönetimi',
    color: 'bg-sky-500',
    bgColor: 'bg-sky-50'
  },
  'settings': {
    icon: Settings,
    title: 'Ayarlar',
    description: 'Sistem ayarları',
    color: 'bg-gray-500',
    bgColor: 'bg-gray-50'
  },
  'decision-support': {
    icon: TrendingUp,
    title: 'Karar Destek',
    description: 'Karar destek sistemi',
    color: 'bg-fuchsia-500',
    bgColor: 'bg-fuchsia-50'
  }
};

// Öncelik sırası - hangi kartlar önce gösterilsin
const PAGE_PRIORITY = [
  'calculator', 'price-lists', 'crm', 'tasks', 'insurance', 
  'bonus', 'preorders', 'purchases', 'procurement-decision',
  'performance', 'refunds', 'campaigns', 'brands', 'models',
  'factory-prices', 'branches', 'users', 'settings', 'decision-support'
];

export default function RoleBasedDashboard() {
  const { user, accessiblePages, isSuperAdmin, canAccessPage } = usePermissions();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Temel istatistikleri yükle
      const [notifRes] = await Promise.all([
        api.get('/notifications?limit=5').catch(() => ({ data: [] }))
      ]);
      setNotifications(notifRes.data || []);
    } catch (error) {
      console.error('Dashboard data yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcının erişebildiği ve tanımlı olan sayfaları filtrele ve sırala
  const availablePages = PAGE_PRIORITY
    .filter(pageId => {
      // Kullanıcı bu sayfaya erişebilir mi?
      if (!canAccessPage(pageId)) return false;
      // Sayfa tanımı var mı?
      if (!PAGE_DEFINITIONS[pageId]) return false;
      // Dashboard sayfasını gösterme
      if (pageId === 'dashboard') return false;
      return true;
    })
    .slice(0, 12); // Maksimum 12 kart göster

  const QuickLinkCard = ({ pageId }) => {
    const page = PAGE_DEFINITIONS[pageId];
    if (!page) return null;
    
    const Icon = page.icon;
    
    return (
      <Card 
        className={`p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer border-l-4 ${page.bgColor}`}
        style={{ borderLeftColor: page.color.replace('bg-', '').replace('-500', '') }}
        onClick={() => navigate(`/${pageId}`)}
        data-testid={`quick-link-${pageId}`}
      >
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${page.color} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg mb-1">{page.title}</h3>
            <p className="text-sm text-muted-foreground">{page.description}</p>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center md:text-left">
          <h1 className="text-3xl font-bold">
            Hoş Geldiniz, <span className="text-primary">{user?.name || user?.username}</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            {isSuperAdmin ? 'Sistem Yöneticisi' : user?.role_template || 'Kullanıcı'} olarak giriş yaptınız
          </p>
        </div>

        {/* Bildirimler */}
        {notifications.length > 0 && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Bildirimler</h3>
                <p className="text-sm text-blue-700">
                  {notifications.length} yeni bildiriminiz var
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto"
                onClick={() => navigate('/notifications')}
              >
                Görüntüle
              </Button>
            </div>
          </Card>
        )}

        {/* Hızlı Erişim */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Hızlı Erişim</h2>
            <Badge variant="outline" className="text-muted-foreground">
              {availablePages.length} modül erişiminiz var
            </Badge>
          </div>
          
          {availablePages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availablePages.map(pageId => (
                <QuickLinkCard key={pageId} pageId={pageId} />
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">Henüz modül erişiminiz yok</h3>
              <p className="text-sm text-muted-foreground">
                Yöneticinizle iletişime geçerek yetki talep edebilirsiniz
              </p>
            </Card>
          )}
        </div>

        {/* Yardım */}
        <Card className="p-6 bg-gradient-to-r from-slate-50 to-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-medium">Yetki Bilgisi</h3>
              <p className="text-sm text-muted-foreground">
                Yukarıda sadece size tanımlanan modüller gösterilmektedir. 
                Ek yetki için sistem yöneticinize başvurun.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
